import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { SUMMARY } from '../../../../../ui-kitchen/src/constants/bookingFlow';
import { handleBookingError } from './reservationDetailsUtils';
import { getVenue } from '../../../../../ui-kitchen/src/api/api';
import { venueTransform } from '../../../../../ui-kitchen/src/api/resyApiTransformer';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

/* eslint-disable import/prefer-default-export */
const useAuthenticatedEffect = ({
  isAuthenticated, getUserThunk, resetPayment, setBookingFlowState,
}) => {
  useEffect(() => {
    if (isAuthenticated) {
      getUserThunk(true);
    } else {
      // user logs out in the middle of the reservation flow
      resetPayment();
      // in the case we're on the add payment screen
      // we want to ensure we're back on the summary screen
      setBookingFlowState(SUMMARY);
    }
  }, [isAuthenticated]);
};

const useHandleBookingError = ({
  backPath,
  clearMessages,
  errorBook,
  resetBookingError,
  setMessages,
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (errorBook) {
      handleBookingError({
        backPath,
        clearMessages,
        errorBook,
        navigate,
        resetBookingError,
        setMessages,
      });
    }
  }, [errorBook]);
};

const useClearMessages = ({ isIdleOrPending, messages, clearMessages }) => {
  useEffect(() => {
    if (isIdleOrPending && !isEmpty(messages)) {
      clearMessages();
    }
  }, [isIdleOrPending]);
};

const useFetchVenue = ({ venueId, setDetailsState }) => {
  useEffect(() => {
    const fetchVenue = () => getVenue({ id: venueId })
      .then((data) => {
        setDetailsState((prevState) => ({ ...prevState, venueState: venueTransform(data) }));
      })
      .catch((error) => Logger.error('[VenuePage] useFetchVenue() failure: ', error));
    if (venueId) {
      fetchVenue();
    }
  }, [venueId]);
};

const useTransformPaymentMethods = ({
  cardLabel,
  paymentMethods,
  selectedPaymentMethodId,
  setDetailsState,
  setPaymentId,
}) => {
  useEffect(() => {
    if (paymentMethods !== null && paymentMethods?.length) {
      const resultList = [];
      for (let i = 0; i < paymentMethods.length; i += 1) {
        const currentPayment = paymentMethods[i];
        resultList.push({
          label: `${cardLabel} ${currentPayment.display}`,
          value: currentPayment.id,
          type: currentPayment.type,
        });
        if (currentPayment.is_default) {
          setPaymentId(currentPayment.id);
        }
      }
      setDetailsState((prevState) => ({ ...prevState, paymentMethodsList: resultList }));
      if (selectedPaymentMethodId === undefined) {
        setPaymentId(resultList[0]?.value);
      }
    }
  }, [paymentMethods]);
};

const useSetServiceCharge = ({
  initServiceCharge,
  setDetailsState,
  setServiceCharge,
  serviceCharge,
  serviceChargeOptionsConfig,
}) => {
  useEffect(() => {
    if (!initServiceCharge && serviceChargeOptionsConfig) {
      setDetailsState((prevState) => ({ ...prevState, initServiceCharge: true }));
      const defaultServiceCharge = serviceChargeOptionsConfig.find((option) => (
        option.is_default === 1 || option.is_default === true
      ));
      if (!serviceCharge && defaultServiceCharge) {
        setServiceCharge(defaultServiceCharge.amount);
      }
    }
  }, [initServiceCharge, serviceCharge, serviceChargeOptionsConfig]);
};

export {
  useAuthenticatedEffect,
  useClearMessages,
  useFetchVenue,
  useHandleBookingError,
  useSetServiceCharge,
  useTransformPaymentMethods,
};
